import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Medication, RxDispenseEvent, LabelSize } from '../../services/medicationService';
import { formatCalendarDate } from '../../utils/dateUtils';

/**
 * RxLabel — renders an Rx label sized for one of the supported DYMO label
 * sizes. Shared by:
 *   - Settings → Printers (test label with hardcoded sample data)
 *   - RxDispenseDialog (real Rx + dispense event, printed after dispense)
 *
 * On-screen preview only. Printing goes through `printRxLabels` in
 * ./printLabel.ts, which renders a dependency-free copy of this layout into a
 * hidden iframe — see that file for why the old in-document @media print
 * approach produced dozens of blank pages.
 *
 * Pass either a `medication` (real Rx) or `sample` data (test mode). When a
 * dispense event is also provided, its instructionsOverride wins over
 * Medication.instructions for the sig line.
 */

export interface RxLabelSampleData {
  clinic: { name: string; address: string; phone: string; logoUrl?: string };
  vet: { name: string; license?: string; deaNumber?: string };
  patient: { name: string; species: string; owner: string };
  rx: {
    drug: string;
    quantity: string;
    sig: string;
    issued: string;
    expires: string;
    refillsRemaining: number;
    refillsAuthorized: number;
    schedule?: string; // e.g. "C-IV"
  };
  warnings?: string[];
}

interface SizeConfig {
  widthMm: number;
  heightMm: number;
  showCautionaryStatement: boolean;
  showWarnings: boolean;
}

export const RX_LABEL_SIZES: Record<LabelSize, SizeConfig> = {
  DYMO_30252: {
    widthMm: 89,
    heightMm: 28,
    showCautionaryStatement: false,
    showWarnings: false,
  },
  DYMO_30256: {
    widthMm: 102,
    heightMm: 59,
    showCautionaryStatement: true,
    showWarnings: true,
  },
  // Smaller sizes — exposed in the type system but not yet templated. Fall
  // back to 30252 layout proportions until per-size templates ship.
  DYMO_30334: {
    widthMm: 57,
    heightMm: 32,
    showCautionaryStatement: false,
    showWarnings: false,
  },
  DYMO_30327: {
    widthMm: 87,
    heightMm: 14,
    showCautionaryStatement: false,
    showWarnings: false,
  },
};

const FEDERAL_CAUTIONARY_STATEMENT =
  'CAUTION: Federal law prohibits the transfer of this drug to any person other than the patient for whom it was prescribed.';

interface RxLabelProps {
  size: LabelSize;
  /** Real Rx data path. Mutually exclusive with `sample`. */
  medication?: Medication;
  dispenseEvent?: RxDispenseEvent;
  /** Test/preview path with hardcoded data. Mutually exclusive with `medication`. */
  sample?: RxLabelSampleData;
  /** Multiplier for on-screen rendering only; print uses physical mm. Default 2.5. */
  previewScale?: number;
  /** Owner full name as fallback when no horse/owners json is present. */
  ownerNameFallback?: string;
  /** Clinic name/address/phone/logo, when rendering from a real Medication that lacks the relation. */
  clinicOverride?: { name?: string; address?: string; phone?: string; logoUrl?: string };
}

/**
 * Issued (`Medication.startDate`) and Expires (`Medication.expirationDate`) on a
 * PRINTED Rx label — both CALENDAR DAYS (DATE-KINDS.md §1.2 / §2.2), so both are
 * rendered from the date PART and read the same in every zone.
 *
 * ⚠ This site was invisible to `scripts/check-date-idioms.js`: the banned idiom
 * is `new Date(x).toLocaleDateString()` on ONE line, and this spelled it across
 * three (`const d = new Date(iso)` … `d.toLocaleDateString()`). It is the same
 * bug the ratchet exists for, on the one surface a client physically takes home
 * — so it is fixed here even though no baseline entry counted it.
 */
export const formatLabelCalendarDate = (iso?: string | null): string =>
  formatCalendarDate(iso, 'M/d/yyyy');

const extractFirstOwnerName = (medication?: Medication): string => {
  const owners = (medication?.horse as any)?.owners;
  if (Array.isArray(owners) && owners.length > 0 && owners[0]?.name) {
    return owners[0].name;
  }
  return '';
};

/**
 * Builds the rendered fields from either a real Medication+event or a sample.
 * Pure — no React state — so the print and preview paths render identical DOM.
 * Exported so the iframe print path (./printLabel.ts) and the dispense dialogs
 * can turn a Medication + RxDispenseEvent into label data without a render.
 */
export function buildLabelData(props: RxLabelProps): RxLabelSampleData {
  if (props.sample) return props.sample;

  const med = props.medication;
  const evt = props.dispenseEvent;

  if (!med) {
    // Shouldn't happen given the prop union but render a placeholder rather
    // than crash if some upstream prop wiring forgets to pass anything.
    return {
      clinic: { name: '—', address: '—', phone: '—' },
      vet: { name: '—' },
      patient: { name: '—', species: 'Equine', owner: '—' },
      rx: {
        drug: '—',
        quantity: '',
        sig: '',
        issued: '',
        expires: '',
        refillsRemaining: 0,
        refillsAuthorized: 0,
      },
    };
  }

  const clinicData = (med.clinic as any) || {};
  const horseData = (med.horse as any) || {};
  const refillsAuthorized = med.refillsAuthorized ?? 0;
  // Model B: total fills allowed = refillsAuthorized + 1 (initial + refills)
  // dispenseEventCount reflects fills DISPENSED so far including this one.
  const totalFillsAllowed = refillsAuthorized + 1;
  const fillsDispensed = med._count?.dispenseEvents ?? 0;
  const sigSource = evt?.instructionsOverride || med.instructions || '';

  return {
    clinic: {
      name: props.clinicOverride?.name ?? clinicData.name ?? '',
      address: props.clinicOverride?.address ?? clinicData.address ?? '',
      phone: props.clinicOverride?.phone ?? clinicData.phone ?? '',
      // Blank/absent → undefined so the header simply omits the logo slot.
      logoUrl: props.clinicOverride?.logoUrl || clinicData.logoUrl || undefined,
    },
    vet: {
      name: med.prescribedBy || '',
    },
    patient: {
      name: horseData.name || '',
      species: horseData.species || 'Equine',
      owner: props.ownerNameFallback || extractFirstOwnerName(med),
    },
    rx: {
      drug: [med.name, med.dosage].filter(Boolean).join(' '),
      quantity: evt
        ? `${parseFloat(evt.quantity).toString()} ${evt.unit}`
        : med.quantity
          ? `${med.quantity}`
          : '',
      sig: sigSource,
      issued: formatLabelCalendarDate(med.startDate),
      expires: formatLabelCalendarDate(med.expirationDate),
      // Kept as "refills" on the printed label for backward compat with the
      // sample-data path (which has refillsRemaining/refillsAuthorized
      // hardcoded). Real Rxs map to "Fills X of Y" via the values below.
      refillsRemaining: fillsDispensed,
      refillsAuthorized: totalFillsAllowed,
      schedule: med.deaSchedule ? `C-${med.deaSchedule}` : undefined,
    },
  };
}

const RxLabel: React.FC<RxLabelProps> = (props) => {
  const cfg = RX_LABEL_SIZES[props.size];
  const previewScale = props.previewScale ?? 2.5;
  const data = buildLabelData(props);

  return (
    <Paper
      className="print-label"
      elevation={2}
      sx={{
        width: `${cfg.widthMm * previewScale}px`,
        height: `${cfg.heightMm * previewScale}px`,
        p: `${1.5 * previewScale}px`,
        bgcolor: '#fff',
        color: '#000',
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: `${0.4 * previewScale}px`,
        lineHeight: 1.15,
      }}
    >
      {/* Clinic header */}
      <Box
        sx={{
          borderBottom: '1px solid #000',
          pb: `${0.3 * previewScale}px`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${previewScale}px`,
        }}
      >
        {data.clinic.logoUrl && (
          <Box
            component="img"
            src={data.clinic.logoUrl}
            alt=""
            sx={{
              maxHeight: `${6 * previewScale}px`,
              maxWidth: `${20 * previewScale}px`,
              objectFit: 'contain',
              flex: '0 0 auto',
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: `${2.4 * previewScale}px`, fontWeight: 700, lineHeight: 1 }}>
            {data.clinic.name}
          </Typography>
          <Typography sx={{ fontSize: `${1.7 * previewScale}px`, lineHeight: 1.1 }}>
            {[data.clinic.address, data.clinic.phone].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      </Box>

      {/* Patient + Drug */}
      <Box>
        <Typography sx={{ fontSize: `${1.8 * previewScale}px`, lineHeight: 1.15 }}>
          <strong>Patient:</strong> {data.patient.name}
          {data.patient.species && ` (${data.patient.species})`}
          {data.patient.owner && (
            <>
              {' '}&middot; <strong>Owner:</strong> {data.patient.owner}
            </>
          )}
        </Typography>
        <Typography sx={{ fontSize: `${2.1 * previewScale}px`, fontWeight: 700, lineHeight: 1.15 }}>
          {data.rx.drug}
          {cfg.showCautionaryStatement && data.rx.schedule ? ` [${data.rx.schedule}]` : ''}
        </Typography>
        <Typography sx={{ fontSize: `${1.8 * previewScale}px`, lineHeight: 1.15 }}>
          {data.rx.quantity && (
            <>
              <strong>Qty:</strong> {data.rx.quantity}{' '}&middot;{' '}
            </>
          )}
          <strong>Sig:</strong> {data.rx.sig}
        </Typography>
      </Box>

      {/* Warnings (large sizes only) */}
      {cfg.showWarnings && props.sample?.warnings && props.sample.warnings.length > 0 && (
        <Box>
          {props.sample.warnings.map((w, i) => (
            <Typography key={i} sx={{ fontSize: `${1.5 * previewScale}px`, lineHeight: 1.15, fontStyle: 'italic' }}>
              ⚠ {w}
            </Typography>
          ))}
        </Box>
      )}

      {/* Federal cautionary statement (large + controlled) */}
      {cfg.showCautionaryStatement && data.rx.schedule && (
        <Box
          sx={{
            border: '1px solid #000',
            p: `${0.4 * previewScale}px`,
            bgcolor: '#f5f5f5',
          }}
        >
          <Typography sx={{ fontSize: `${1.4 * previewScale}px`, lineHeight: 1.1, fontWeight: 600 }}>
            {FEDERAL_CAUTIONARY_STATEMENT}
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 'auto', borderTop: '1px solid #000', pt: `${0.3 * previewScale}px` }}>
        <Typography sx={{ fontSize: `${1.6 * previewScale}px`, lineHeight: 1.1 }}>
          {data.vet.name && (
            <>
              <strong>Rx by:</strong> {data.vet.name}
              {data.vet.license && ` (${data.vet.license}${cfg.showCautionaryStatement && data.vet.deaNumber ? `, DEA ${data.vet.deaNumber}` : ''})`}{' '}&middot;{' '}
            </>
          )}
          {data.rx.issued && (
            <>
              <strong>Issued:</strong> {data.rx.issued}{' '}&middot;{' '}
            </>
          )}
          {data.rx.expires && (
            <>
              <strong>Expires:</strong> {data.rx.expires}{' '}&middot;{' '}
            </>
          )}
          <strong>Fill:</strong> {data.rx.refillsRemaining} of {data.rx.refillsAuthorized}
        </Typography>
      </Box>
    </Paper>
  );
};

export default RxLabel;
